import React, { useRef, useMemo } from 'react';
import { CircularProgress, Box } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { UserPermissionTable } from './tables/UserPermissionTable';

interface UserDetailProps {
  user: any; // Replace with your actual user type
  onClose: () => void;
  open: boolean;
  isLoading?: boolean;
}

export const UserDetail: React.FC<UserDetailProps> = ({ user, onClose, open, isLoading = false }) => {
  const permissionTableRef = useRef<any>(null);

  // Memoize permissions to prevent unnecessary re-renders that could disrupt editing
  // Use JSON.stringify for deep comparison to catch all changes
  const stablePermissions = useMemo(() => {
    const permissions = user?.userPermissionList || [];
    return permissions;
  }, [JSON.stringify(user?.userPermissionList)]);

  if (!open) {
    return null;
  }

  if (isLoading) {
    return (
      <div className='absolute right-0 top-12 bottom-0 w-full max-w-[720px] bg-white border border-neutral-20 border-r-0 z-50 overflow-y-auto'>
        <Box display='flex' justifyContent='center' alignItems='center' p={4}>
          <CircularProgress color='primary' />
        </Box>
      </div>
    );
  }

  if (!user) {
    return (
      <div className='absolute right-0 top-12 bottom-0 w-full max-w-[720px] bg-white border border-neutral-20 border-r-0 z-50 overflow-y-auto'>
        <div className='p-4'>
          <p className='text-base'>No user information found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className='absolute right-0 top-12 bottom-0 w-full max-w-[720px] bg-white border border-neutral-20 border-r-0 z-50 overflow-y-auto'>
      {/* Permissions Section */}
      <div className='relative border-b border-neutral-20 mb-[24px]'>
        <div className='p-24 flex items-center justify-between'>
          <div>
            <h3 className='text-20 font-bold mb-2'>Permissions</h3>
            <p className='text-14 text-neutral-80'>User Permission List</p>
          </div>
          <div className='flex items-center gap-12 ml-auto'>
            <IconButton
              onClick={() => {
                // Get reference to the permission table component and trigger create mode
                if (permissionTableRef.current) {
                  permissionTableRef.current.setCreatingRow(true);
                }
              }}
              sx={{
                width: '30px',
                height: '30px',
                border: '1px solid #DFDFDF',
                borderRadius: '50%',
                '&:hover': {
                  backgroundColor: '#00992B',
                  '& .MuiSvgIcon-root': {
                    color: '#FFFFFF',
                  },
                },
              }}
            >
              <AddIcon sx={{ color: '#00A651', fontSize: 24 }} />
            </IconButton>

            <IconButton
              aria-label='close'
              onClick={onClose}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </div>
        </div>
        <UserPermissionTable key={`permissions-table-${user.id}`} permissions={stablePermissions} userId={user.id || ''} ref={permissionTableRef} />
      </div>
    </div>
  );
};
